// One agent on the Hub (spec 14 §4.2, D-512 rulings 3, 4, 5 and 9). Every value
// is a field of `GET /v1/agents`'s item; nothing is computed that the server
// did not serve, and money the reader may not see is not drawn at all.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { toast } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { useRefName } from "../../../../lib/ref-names";
import { formatMinor } from "../../inventory/lib/cost";
import { agentsKeys, patchAgent } from "../hooks/agents-queries";
import {
  canToggleStatus,
  meterShare,
  sparklinePoints,
  spendMeter,
  toggledStatus,
  type AgentListItem,
} from "../lib/agents-lib";
import { agentName, errorText } from "../lib/labels";

const STATUS_VARIANT: Record<AgentListItem["status"], ChipVariant> = {
  enabled: "ok",
  paused: "neutral",
  disabled: "bad",
};

export function Meter({ share, full }: { share: number | undefined; full?: boolean }) {
  if (share === undefined) return null;
  return (
    <span className="ag-meter" aria-hidden="true">
      <span
        className="ag-meter-fill"
        data-full={full === true || share >= 1 ? "true" : "false"}
        style={{ width: `${Math.round(share * 100)}%` }}
      />
    </span>
  );
}

function Sparkline({ agent, label }: { agent: AgentListItem; label: string }) {
  const points = sparklinePoints(agent.activity_30d, 120, 24);
  const total = agent.activity_30d.reduce((sum, d) => sum + d.actions, 0);
  return (
    <svg
      className="ag-spark"
      viewBox="0 0 120 28"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${label}: ${total.toLocaleString()}`}
      data-testid="agent-spark"
    >
      {points.length > 0 ? <polyline points={points} transform="translate(0 2)" /> : null}
    </svg>
  );
}

function PausedLine({ agent }: { agent: AgentListItem }) {
  const { t, i18n } = useTranslation();
  const who = useRefName("user", agent.status_changed_by);
  if (agent.status !== "paused" || agent.status_changed_at === null) return null;
  const date = formatTimestamp(agent.status_changed_at, i18n.language);
  return (
    <p className="ag-card-paused" data-testid="agent-paused-by">
      {who.name !== undefined
        ? t("agents.status.pausedBy", { name: who.name, date })
        : t("agents.status.pausedOn", { date })}
    </p>
  );
}

export function AgentCard({
  agent,
  canManage,
  canConfigure,
}: {
  agent: AgentListItem;
  canManage: boolean;
  canConfigure: boolean;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const name = agentName(t, agent);
  const spend = spendMeter(agent);
  const actionShare = meterShare(
    agent.usage_mtd.actions_unsupervised,
    agent.caps.monthly_action_cap,
  );
  const tokenShare = meterShare(agent.tokens_today, agent.caps.daily_token_budget);
  const toggleable = canToggleStatus(agent, canManage);
  const grey = agent.status !== "enabled";

  const toggle = async () => {
    const next = toggledStatus(agent.status);
    setBusy(true);
    try {
      await patchAgent(agent.id, { status: next, version: agent.version });
      toast.success(t("agents.hub.statusSaved", { name, status: t(`agents.status.${next}`) }));
    } catch (error) {
      toast.error(errorText(t, error));
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: agentsKeys.all });
    }
  };

  return (
    <Card data-testid="agent-card">
      <Card.Body className="ag-stack">
        <div className="ag-card-head">
          <h2
            className={`ag-card-name${grey ? " ag-card-grey" : ""}`}
            data-testid="agent-card-name"
          >
            {name}
          </h2>
          <StatusChip variant={STATUS_VARIANT[agent.status]} data-testid="agent-status">
            {t(`agents.status.${agent.status}`)}
          </StatusChip>
        </div>
        <PausedLine agent={agent} />
        <Sparkline agent={agent} label={t("agents.hub.activity")} />
        <dl className="ag-metrics">
          <div className="ag-metric" data-testid="agent-actions-mtd">
            <dt>{t("agents.hub.actions")}</dt>
            <dd>
              {agent.caps.monthly_action_cap > 0
                ? t("agents.hub.actionsOf", {
                    used: agent.usage_mtd.actions_unsupervised.toLocaleString(i18n.language),
                    cap: agent.caps.monthly_action_cap.toLocaleString(i18n.language),
                  })
                : t("agents.hub.actionsNoCap", {
                    used: agent.usage_mtd.actions_unsupervised.toLocaleString(i18n.language),
                  })}
            </dd>
            <Meter share={actionShare} />
          </div>
          {spend.spendMinor !== undefined || spend.capReached ? (
            <div className="ag-metric" data-testid="agent-spend-mtd">
              <dt>{t("agents.hub.spend")}</dt>
              {spend.spendMinor !== undefined ? (
                <dd>
                  {t("agents.hub.spendOf", {
                    used: formatMinor(spend.spendMinor, spend.currency, i18n.language),
                    cap: formatMinor(spend.capMinor, agent.caps.spend_cap_currency, i18n.language),
                  })}
                </dd>
              ) : null}
              <Meter share={spend.share} full={spend.capReached} />
              {spend.capReached ? (
                <dd>
                  <StatusChip variant="bad" data-testid="agent-cap-reached">
                    {t("agents.hub.capReached")}
                  </StatusChip>
                </dd>
              ) : null}
            </div>
          ) : null}
          <div className="ag-metric" data-testid="agent-tokens-today">
            <dt>{t("agents.hub.tokens")}</dt>
            <dd>
              {t("agents.hub.tokensOf", {
                used: agent.tokens_today.toLocaleString(i18n.language),
                budget: agent.caps.daily_token_budget.toLocaleString(i18n.language),
              })}
            </dd>
            <Meter share={tokenShare} />
          </div>
          <div className="ag-metric" data-testid="agent-last-run">
            <dt>{t("agents.hub.lastRun")}</dt>
            <dd>
              {agent.last_run === null
                ? t("agents.hub.lastRunNone")
                : `${t(`agents.runStatus.${agent.last_run.status}`)}${
                    agent.last_run.finished_at !== null
                      ? ` · ${formatTimestamp(agent.last_run.finished_at, i18n.language)}`
                      : ""
                  }`}
            </dd>
          </div>
        </dl>
        <div className="ag-card-actions">
          <Link
            to="/agents/journal"
            search={{ agent: agent.id }}
            className="ag-link"
            data-testid="agent-view-journal"
          >
            {t("agents.hub.viewJournal")}
          </Link>
          {canConfigure ? (
            <Link
              to="/agents/$agentId"
              params={{ agentId: agent.id }}
              className="ag-link"
              data-testid="agent-configure"
            >
              {t("agents.hub.configure")}
            </Link>
          ) : null}
          {toggleable ? (
            <label className="ag-toggle">
              <input
                type="checkbox"
                role="switch"
                checked={agent.status === "enabled"}
                disabled={busy}
                onChange={() => {
                  void toggle();
                }}
                aria-label={t("agents.hub.toggleLabel", { name })}
                data-testid="agent-status-toggle"
              />
              {agent.status === "enabled" ? t("agents.hub.pause") : t("agents.hub.enable")}
            </label>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
}
